import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, shareReplay } from 'rxjs';
import { takeUntil, tap, catchError } from 'rxjs/operators';
import { AuthService, UserRole } from './auth.service';
import { SidebarItem } from '../common-components/sidebar/sidebar.component';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class SidebarService implements OnDestroy {
  private readonly sidebarItemsSubject = new BehaviorSubject<SidebarItem[]>([]);
  public sidebarItems$: Observable<SidebarItem[]> = this.sidebarItemsSubject.asObservable();
  
  // Cache the config using shareReplay
  private static config$: Observable<Record<string, SidebarItem[]>> | null = null;
  private static configCache: Record<string, SidebarItem[]> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly configUrl = 'assets/data/sidebar-items.json';

  constructor(private readonly authService: AuthService, private readonly http: HttpClient) {
    
    // Initialize config$ if not already initialized
    if (!SidebarService.config$) {
      SidebarService.config$ = this.http.get<Record<string, SidebarItem[]>>(this.configUrl)
        .pipe(
          tap(config => {
            // Cache the config
            SidebarService.configCache = config;
          }),
          catchError(error => {
            console.error('[SidebarService] Error loading config:', error);
            return [];
          }),
          shareReplay(1) // Share the result with all subscribers
        );
    }

    // Subscribe to user changes
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          this.updateSidebarItems(user.role);
        } else {
          this.sidebarItemsSubject.next([]);
        }
      });
  }

  private updateSidebarItems(role: UserRole): void {
    // First try to use cached config
    if (SidebarService.configCache) {
      const items = SidebarService.configCache[role.toUpperCase()] || [];
      this.sidebarItemsSubject.next(items);
      return;
    }

    // If no cache, use the observable
    if (!SidebarService.config$) {
      console.error('[SidebarService] Config not loaded');
      this.sidebarItemsSubject.next([]);
      return;
    }

    // Subscribe to config$ only if we don't have cache
    SidebarService.config$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (config) => {
        const items = config[role.toUpperCase()] || [];
        this.sidebarItemsSubject.next(items);
      },
      error: (error) => {
        console.error('[SidebarService] Error loading sidebar items:', error);
        this.sidebarItemsSubject.next([]);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
